import * as React from "react";
import { Button, Space } from "antd";
import { ColumnProps } from "antd/lib/table";
import { FileTypeIcon } from "Pages/Project/Documents/Components/FileTypeIcon";
import { FormatData } from "utils/FormatData";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { TableRowSelection } from "antd/lib/table/interface";
import { API } from "General/API";
import { projectsDataStore } from "Stores";
import { API_URL } from "General/APIEndpoints";
import { download } from "utils/download";
import { stringSort, numberSort } from "utils/defaultSort";
import { Table } from "Components/General/Table";

interface DownloadEmailAttachmentsProps {
  mailData: API_MailResponse;
}

export const DownloadEmailAttachments = ({ mailData = {} }: DownloadEmailAttachmentsProps) => {
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [allLoading, setAllLoading] = React.useState(false);
  const { t } = useTranslation(I18N_NS.PROJECT);

  const allowedToDownloadFiles = React.useMemo(
    () => mailData?.attachments.filter((item) => item.fileActions?.download) || [],
    [mailData.attachments]
  );

  const handleRowSelectionChange: TableRowSelection<API_FileAttachment>["onChange"] = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const downloadSelectedFiles = async () => {
    try {
      setLoading(true);
      const formData: API_DownloadWizardProperties = {
        files: selectedRowKeys.map((item) => ({ id: +item, state: "ACTIVE" })),
        withDirectories: false,
        withWatermark: false,
        withQR: projectsDataStore.isQRForce(),
        pdfWherePossible: false,
      };
      const file = await API.getBlobByPost(
        API_URL.FILES.getDownloadWizard2Response(projectsDataStore.projectId),
        formData
      );
      download(file, file.name);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const downloadAllFiles = React.useCallback(async () => {
    try {
      setAllLoading(true);
      const formData: API_DownloadWizardProperties = {
        files: allowedToDownloadFiles.map((item) => ({ id: +item.id, state: "ACTIVE" })),
        withDirectories: false,
        withWatermark: false,
        withQR: projectsDataStore.isQRForce(),
        pdfWherePossible: false,
      };
      const file = await API.getBlobByPost(
        API_URL.FILES.getDownloadWizard2Response(projectsDataStore.projectId),
        formData
      );
      download(file, file.name);
    } catch (error) {
      throw error;
    } finally {
      setAllLoading(false);
    }
  }, [allowedToDownloadFiles?.length]);

  const columns: ColumnProps<API_FileAttachment>[] = [
    {
      dataIndex: "type",
      width: 30,
      render: (text, record) => <FileTypeIcon state={"ACTIVE"} relation={[]} type={record.type} malware={false} />,
    },
    {
      title: t("Name"),
      dataIndex: "name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => stringSort(a, b, "name"),
    },
    {
      title: t("Size"),
      dataIndex: "size",
      width: 100,
      render: (text, record) => record.size && FormatData.formatBytes(record.size, 2),
      sorter: (a, b) => numberSort(a.size, b.size),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" onClick={downloadSelectedFiles} disabled={!selectedRowKeys.length} loading={loading}>
            {t("Download selected files")}
          </Button>

          <Button onClick={downloadAllFiles} loading={allLoading}>
            {t("Download all")}
          </Button>
        </Space>
      </div>
      <Table<API_FileAttachment>
        name="downloadEmailAttach"
        dataSource={allowedToDownloadFiles}
        pagination={{ hideOnSinglePage: true, showSizeChanger: true }}
        rowKey="id"
        columns={columns}
        rowSelection={{
          selectedRowKeys,
          onChange: handleRowSelectionChange,
          getCheckboxProps: (record) => ({
            disabled: record.deleted || record.malware,
          }),
        }}
      />
    </div>
  );
};
